import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Calendar, ArrowRight, Filter, Clock, User, Tag } from 'lucide-react';
import { useArticles } from '../hooks/useApi';
import analytics from '../utils/analytics';
import { usePageTitle } from '../hooks/usePageTitle';

const ArticlesPage: React.FC = () => {
  // 设置页面标题和描述
  usePageTitle(
    '技术交流',
    '窑炉技术、加热设备及加热技术、节能环保技术及经验交流'
  );

  // 从静态 JSON 获取文章数据
  const { data: articles, loading, error } = useArticles({
    sort: 'date',
    limit: 50
  });

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // 加载状态
  if (loading) {
    return (
      <div className="pt-16">
        <section className="section-padding bg-gradient-primary">
          <div className="container-max text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              技术交流
            </h1>
            <p className="text-xl text-white/90 max-w-3xl mx-auto">
              窑炉技术、加热设备及加热技术、节能环保技术及经验交流
            </p>
          </div>
        </section>
        <section className="section-padding bg-white">
          <div className="container-max">
            <div className="flex justify-center items-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
              <span className="ml-4 text-gray-600">加载中...</span>
            </div>
          </div>
        </section>
      </div>
    );
  }

  // 错误状态
  if (error) {
    return (
      <div className="pt-16">
        <section className="section-padding bg-gradient-primary">
          <div className="container-max text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              技术交流
            </h1>
            <p className="text-xl text-white/90 max-w-3xl mx-auto">
              窑炉技术、加热设备及加热技术、节能环保技术及经验交流
            </p>
          </div>
        </section>
        <section className="section-padding bg-white">
          <div className="container-max">
            <div className="text-center py-20">
              <div className="text-red-500 mb-4">
                <Filter className="w-16 h-16 mx-auto" />
              </div>
              <h3 className="text-xl font-semibold text-gray-600 mb-2">
                加载失败
              </h3>
              <p className="text-gray-500 mb-4">
                {error}
              </p>
              <button 
                onClick={() => window.location.reload()} 
                className="btn-primary"
              >
                重新加载
              </button>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="pt-16">
      {/* Hero Section */}
      <section className="section-padding bg-gradient-primary">
        <div className="container-max text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              技术交流
            </h1>
            <p className="text-xl text-white/90 max-w-3xl mx-auto">
              窑炉技术、加热设备及加热技术、节能环保技术及经验交流
            </p>
          </motion.div>
        </div>
      </section>

      {/* Articles Section */}
      <section className="section-padding bg-white">
        <div className="container-max">
          {/* Articles Grid */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {articles.map((article, index) => (
              <motion.div
                key={article.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="card group hover:scale-105 transition-all duration-300"
              >
                <div className="relative overflow-hidden rounded-t-xl">
                  <img
                    src={article.image || '/images/placeholder-article.jpg'}
                    alt={article.title}
                    className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-500"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = '/images/placeholder-article.jpg';
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </div>
                <div className="p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-primary-600 transition-colors duration-200 line-clamp-2">
                    {article.title}
                  </h3>
                  
                  <div className="flex items-center text-sm text-gray-600 mb-3 space-x-4">
                    {(article as any).author && (
                      <div className="flex items-center">
                        <User className="w-4 h-4 mr-1" />
                        <span>{(article as any).author}</span>
                      </div>
                    )}
                    {((article as any).date || article.publishedAt) && (
                      <div className="flex items-center">
                        <Calendar className="w-4 h-4 mr-1" />
                        <span>{formatDate((article as any).date || article.publishedAt!)}</span>
                      </div>
                    )}
                    {(article as any).readTime && (
                      <div className="flex items-center">
                        <Clock className="w-4 h-4 mr-1" />
                        <span>{(article as any).readTime}</span>
                      </div>
                    )}
                  </div>

                  <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                    {(article as any).summary || article.excerpt}
                  </p>

                  {(article as any).tags && (article as any).tags.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-4">
                      {(article as any).tags.slice(0, 3).map((tag: string) => (
                        <span
                          key={tag}
                          className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-600"
                        >
                          <Tag className="w-3 h-3 mr-1" />
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}

                  <Link
                    to={`/articles/${(article as any).slug || article.id}`}
                    onClick={() => analytics.article.clickCard(article.title)}
                    className="inline-flex items-center text-primary-600 hover:text-primary-700 font-medium text-sm"
                  >
                    阅读全文
                    <ArrowRight className="w-4 h-4 ml-1" />
                  </Link>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* Empty State */}
          {articles.length === 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-12"
            >
              <div className="text-gray-400 mb-4">
                <Filter className="w-16 h-16 mx-auto" />
              </div>
              <h3 className="text-xl font-semibold text-gray-600 mb-2">
                暂无文章
              </h3>
              <p className="text-gray-500">
                敬请期待更多技术交流内容
              </p>
            </motion.div>
          )}
        </div>
      </section>

    </div>
  );
};

export default ArticlesPage;
